// src/slices/userManagement/thunk.ts
// Async thunks for User Management

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  toggleUserStatus,
  uploadUserAvatar,
  updateUserPermissions,
} from 'helpers/fakebackend_helper';
import { CreateUserDto, UpdateUserDto } from 'pages/UserManagement/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('user_management_messages');

interface GetUsersParams {
  page?: number;
  pageSize?: number;
  search?: string;
  departmentId?: number;
  roleId?: number;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Fetch users list
export const fetchUsers = createAsyncThunk(
  'UserManagement/fetchUsers',
  async (params: GetUsersParams, { rejectWithValue }) => {
    try {
      const response = await getUsers(params);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message')
      );
    }
  }
);

// Fetch single user by ID
export const fetchUserById = createAsyncThunk(
  'UserManagement/fetchUserById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getUserById(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_2')
      );
    }
  }
);

// Create new user
export const createNewUser = createAsyncThunk(
  'UserManagement/createUser',
  async (data: CreateUserDto, { rejectWithValue }) => {
    try {
      const response = await createUser(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tao_message')
      );
    }
  }
);

// Update existing user
export const updateExistingUser = createAsyncThunk(
  'UserManagement/updateUser',
  async (data: UpdateUserDto, { rejectWithValue }) => {
    try {
      const response = await updateUser(data.id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_cap_message')
      );
    }
  }
);

// Delete user
export const deleteUserById = createAsyncThunk(
  'UserManagement/deleteUser',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteUser(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_xoa_message')
      );
    }
  }
);

// Toggle user status (Active/Locked)
export const toggleStatus = createAsyncThunk(
  'UserManagement/toggleStatus',
  async ({ id, status }: { id: number; status: string }, { rejectWithValue }) => {
    try {
      const response = await toggleUserStatus(id, status);
      const data = response.data || response;
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_thay_message')
      );
    }
  }
);

// Cập nhật Vai trò (RoleId) cho user — popup "Phân quyền người dùng" (mục 8.6, READE.md)
export const updateUserRole = createAsyncThunk(
  'UserManagement/updateUserPermissions',
  async ({ id, roleId }: { id: number; roleId: number }, { rejectWithValue }) => {
    try {
      const response = await updateUserPermissions(id, { roleId, permissions: [] });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_cap_message_2')
      );
    }
  }
);

// Upload user avatar (uploadUserAvatar handles both FileStorage and Identity service)
export const uploadAvatar = createAsyncThunk(
  'UserManagement/uploadAvatar',
  async ({ id, file }: { id: number; file: File }, { rejectWithValue }) => {
    try {
      // uploadUserAvatar already handles 2-step upload
      const response = await uploadUserAvatar(id, file);
      const data = response.data || response;
      
      // Return real FileStorage URL
      return { id, avatarUrl: data.avatarUrl };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_3')
      );
    }
  }
);